<script setup lang="ts">
import ModalComponent from '@src/application/modal-component.vue';
import ToastLayer from '@components/toast-layer.vue';
import modalStore from '@src/application/store/modals-store.ts';
import { isLoading } from '@utils/loader-util.js';
import Menu from '@components/menu/menu.vue';
import mobileDetectStore from '@src/application/store/mobile-detect-store.js';
import TopBar from '@components/top-bar/top-bar.vue';
import { computed, defineAsyncComponent, shallowRef, watch } from 'vue';
import showSideBarStore from '@src/application/store/show-side-bar-store';
import FullScreenLoader from '@components/loader/full-screen-loader.vue';
import Forbidden from '@src/application/forbidden.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import { usePageTitleComposable } from '@src/application/composables/page-title-composable.js';
import { useRouterComposable } from '@router/router-composable.ts';
import { useCurrentUserComposable } from '@src/user/composable/current-user-composable.ts';
import { Dictionary } from '@src/application/dictionary.ts';
import { useTransferRequestPickComposable } from '@src/transfer-request/composable/transfer-request-pick-composable.ts';

const { currentRoute } = useRouterComposable();

function getAsyncComponentOfCurrentRoute() {
    return defineAsyncComponent({
        loader: currentRoute.value.component,
        loadingComponent: FullScreenLoader,
    });
}

const routeComponent = shallowRef(getAsyncComponentOfCurrentRoute());
watch(currentRoute, () => {
    routeComponent.value = getAsyncComponentOfCurrentRoute();
});

const routeProps = shallowRef<Dictionary<string|number|boolean>|null>(null);
watch(currentRoute, async () => {
    if (!currentRoute.value.params || !currentRoute.value.props) {
        routeProps.value = {};
    } else {
        routeProps.value = await currentRoute.value.props(currentRoute.value);
    }
}, { immediate: true, deep: true });

const { currentUser } = useCurrentUserComposable();
const isAllowedRoute = computed<boolean>(() => {
    if (currentUser.value === null) {
        return false;
    }

    if (currentRoute.value === null) {
        return true;
    }

    if (currentRoute.value.meta?.role === undefined) {
        return true;
    }

    if (typeof currentRoute.value.meta.role !== 'string') {
        console.error('invalid configuration - the given role must be string. If there us no role needed just remove it');
    }

    return currentUser.value.roles.includes(currentRoute.value.meta.role as string);
});

function bodyOverflow(hidden: boolean) {
    if (hidden) {
        document.body.style.overflow = 'hidden';
        return;
    }
    document.body.style.overflow = 'visible';
}
modalStore.modals.watch(() => {
    bodyOverflow(modalStore.modals.data.length > 0);
}, { immediate: true, deep: true });

showSideBarStore.showSideBar.watch(() => {
    if (!mobileDetectStore.isMobile.data) {
        return;
    }
    bodyOverflow(showSideBarStore.showSideBar.data);
}, { immediate: true, deep: true });

watch(isLoading, () => {
    bodyOverflow(isLoading());
}, { immediate: true, deep: true });

function checkWindowSize() {
    mobileDetectStore.isMobile.data = window.innerWidth < 640;
}
checkWindowSize();
window.addEventListener('resize', checkWindowSize);

const { pageTitle } = usePageTitleComposable();
watch(currentRoute, () => {
    if (currentRoute.value === null) {
        return;
    }
    const title = currentRoute.value.meta?.title;
    if (!title) {
        return;
    }

    pageTitle.value = (title as string);
}, { immediate: true });

watch(pageTitle, () => {
    let title = pageTitle.value;
    if (title === null) {
        title = 'Backend';
    } else {
        title += ' | Backend';
    }
    document.title = title;
}, { immediate: true });

let pickModalInitialized = false;
watch(currentUser, () => {
    if (pickModalInitialized) {
        return;
    }
    if (currentUser.value === null) {
        return;
    }
    if (!currentUser.value.roles.includes('ROLE_TRANSFER_REQUEST_PICK')) {
        return;
    }
    const { createPickModal } = useTransferRequestPickComposable();
    createPickModal();
    pickModalInitialized = true;
}, { immediate: true });
</script>

<template>
    <div :class="$style.app">
        <FullScreenLoader v-if="isLoading()"/>
        <div v-if="currentUser !== null" :class="$style.layout">
            <Menu :class="$style.not_printable"/>
            <!-- Spread muss an dieser Stelle aus bleiben, weil sonst der Hack width: 1px aus der css Klasse main nicht mehr funktioniert -->
            <VerticalAlignChildrenWrapper gap-bottom="2" :class="$style.main" :spread="false">
                <TopBar :class="$style.not_printable"/>
                <div :class="$style.content_area">
                    <Forbidden v-if="!isAllowedRoute" :forbidden-route="currentRoute"/>
                    <Suspense v-else>
                        <component :is="routeComponent" v-if="routeProps !== null" v-bind="routeProps ?? {}"/>
                        <FullScreenLoader v-else/>
                        <template v-slot:fallback>
                            <FullScreenLoader/>
                        </template>
                    </Suspense>
                </div>
            </VerticalAlignChildrenWrapper>
        </div>
        <ModalComponent v-for="(modal, index) in modalStore.modals.data" v-show="index === 0" :key="modal.uuid" v-bind="modal"/>
        <ToastLayer/>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@sizes' as *;

.app {
    // Jup, etwas verwirrend, aber das overflow ist richtig hier. Ohne das overflow verschiebt sich das top: 0 bei fixed (auf mobilen Geräten) um einige pixel nach oben.
    // Das macht die Positionierung von Objekten innerhalb des Sticky-Wrappers kaputt, so das das erste oben aus dem Viewport rutscht.
    overflow: auto;
    max-width: 100vw;
    min-height: 100vh;
    font-family: $family-sans-serif;
    font-size: $font-size-0;
    font-weight: $weight-normal;
    line-height: 1;
    color: $default-text-color;
    background: #fff;
}
.layout {
    display: flex;
    min-height: 100vh;
}
.main {
    display: block;
    flex: 1 1 0;
    width: 1px; // eklig aber, der ResizeObserver im sticky-wrapper kann anderenfalls seine Breite nicht auslesen, wenn die side-bar zu-/aufgeklappt wird
}
.content_area {
    margin: 0 2em;
}

@media (max-width: 65em) {
    .content_area {
        margin: 0 1em;
    }
}

@media print {
    .not_printable {
        display: none;
    }

    // das gibts hier wegen Chrome, der zeigt sonst immer eine Scroll-Leiste an, das ist von der Größe des Fensters abhängig
    * {
        overflow: hidden;
    }
}
 </style>
